import React from 'react';
import {Box, Chip, Tooltip} from '@mui/material';

/**
 * Two independent liveness signals for a YouTubeProfile, rendered
 * as side-by-side pills:
 *
 *   1. **YouTube** — does the profile currently have a working
 *      YouTube session? Driven by `youtubeSessionValid` (set by
 *      the desktop's periodic pingSession probe). When `null` we
 *      have no signal — render "YouTube unknown".
 *
 *   2. **Worker** — is *any* device (desktop, mobile, ipad…)
 *      currently registered as a worker for this profile and
 *      heartbeating? Driven by `activeWorkers` — server-derived
 *      from devices whose `subscribedProfileIds` includes this
 *      profile, with `isActive == true` when their last
 *      authenticated request was within ~120s. Falls back to the
 *      legacy desktop-only `desktopLastSeenAt` for back-compat
 *      when `activeWorkers` is empty (older clients / edge).
 *
 * Two separate pills (rather than one combined "status") because
 * the failure modes are independent and the fix is different:
 * "YouTube signed out" → re-authenticate on the desktop;
 * "No worker active" → open desktop, mobile, or any subscribed
 * device.
 *
 * Used by the profiles list, the global profile-selector dropdown
 * in Layout, and the per-profile header.
 */
const WORKER_FRESHNESS_MS = 120_000;

interface ActiveWorker {
  deviceId: string;
  name: string;
  type: string;
  lastSeenAt: string;
  isActive: boolean;
}

interface Props {
  profile: {
    youtubeSessionValid?: boolean | null;
    youtubeSessionInvalidReason?: string | null;
    youtubeSessionLastCheckedAt?: string | null;
    youtubeSignedOut?: boolean | null;
    desktopLastSeenAt?: string | null;
    activeWorkers?: ActiveWorker[] | null;
  };
  size?: 'small' | 'medium';
  /** Render in a compact "dot only" style for tight UI like dropdowns. */
  compact?: boolean;
}

function freshness(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Date.now() - t;
}

function relative(iso: string | null | undefined): string {
  const ms = freshness(iso);
  if (ms == null) return 'never';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return new Date(iso!).toLocaleString();
}

// Best-known last-heartbeat time across desktop legacy + the new
// device-based signal. Picks the most-recent across all sources so
// the pill never regresses when both fields are populated.
function mostRecentHeartbeat(
  workers: ActiveWorker[] | null | undefined,
  desktopFallback: string | null | undefined,
): string | null {
  let best: number | null = null;
  let bestIso: string | null = null;
  for (const w of workers ?? []) {
    const t = Date.parse(w.lastSeenAt);
    if (!Number.isNaN(t) && (best == null || t > best)) {
      best = t;
      bestIso = w.lastSeenAt;
    }
  }
  if (desktopFallback) {
    const t = Date.parse(desktopFallback);
    if (!Number.isNaN(t) && (best == null || t > best)) {
      bestIso = desktopFallback;
    }
  }
  return bestIso;
}

export function ProfileLivenessPills({profile, size = 'small', compact = false}: Props): React.JSX.Element {
  // Derive the YouTube state. Falls back to youtubeSignedOut for
  // back-compat in case youtubeSessionValid hasn't arrived yet
  // (legacy task-only writes vs new heartbeat writes).
  const youtubeValid =
    typeof profile.youtubeSessionValid === 'boolean'
      ? profile.youtubeSessionValid
      : profile.youtubeSignedOut === true
        ? false
        : null;

  const workers = profile.activeWorkers ?? [];
  const activeWorkers = workers.filter(w => w.isActive);
  // Fall back to the legacy desktop-only signal when no worker is
  // active AND no worker is even subscribed yet — covers the
  // transition window before mobile + desktop start auto-subscribing.
  const desktopMs = freshness(profile.desktopLastSeenAt);
  const desktopConnectedLegacy =
    workers.length === 0 && desktopMs != null && desktopMs < WORKER_FRESHNESS_MS;
  const anyActive = activeWorkers.length > 0 || desktopConnectedLegacy;
  const lastHeartbeatIso = mostRecentHeartbeat(workers, profile.desktopLastSeenAt);

  const ytTone =
    youtubeValid === true
      ? {bg: '#ECFDF5', fg: '#047857', label: 'YouTube'}
      : youtubeValid === false
        ? {bg: '#FEF2F2', fg: '#B91C1C', label: 'YouTube signed out'}
        : {bg: '#F1F5F9', fg: '#64748B', label: 'YouTube unknown'};

  const workerLabel =
    activeWorkers.length === 1
      ? `Worker · ${activeWorkers[0].name}`
      : activeWorkers.length > 1
        ? `${activeWorkers.length} workers`
        : 'Worker';
  const dtTone = anyActive
    ? {bg: '#ECFDF5', fg: '#047857', label: workerLabel}
    : lastHeartbeatIso
      ? {bg: '#FFFBEB', fg: '#92400E', label: 'No active worker'}
      : {bg: '#F1F5F9', fg: '#64748B', label: 'No worker yet'};

  const ytTooltip =
    youtubeValid === true
      ? `YouTube session valid · last checked ${relative(profile.youtubeSessionLastCheckedAt)}`
      : youtubeValid === false
        ? profile.youtubeSessionInvalidReason ||
          'Desktop reported the YouTube session as signed out — re-authenticate on the desktop app.'
        : 'No desktop heartbeat yet — open the desktop app for this profile to populate this signal.';

  const dtTooltip = anyActive
    ? activeWorkers.length > 0
      ? `Active workers: ${activeWorkers.map(w => `${w.name} (${w.type}, ${relative(w.lastSeenAt)})`).join(', ')}`
      : `Desktop heartbeating · last seen ${relative(profile.desktopLastSeenAt)}`
    : lastHeartbeatIso
      ? `No active worker — last heartbeat ${relative(lastHeartbeatIso)}.`
      : 'No worker has subscribed to this profile yet. Open the desktop or mobile app for this kid.';

  if (compact) {
    // Dot-only mode for the global profile-selector dropdown where
    // a chip pair would be too noisy. Two stacked dots with
    // descriptive tooltips.
    return (
      <Box sx={{display: 'inline-flex', gap: 0.5}}>
        <Tooltip title={ytTooltip}>
          <Box
            sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: ytTone.fg, flexShrink: 0}}
          />
        </Tooltip>
        <Tooltip title={dtTooltip}>
          <Box
            sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: dtTone.fg, flexShrink: 0}}
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{display: 'inline-flex', gap: 0.5, flexWrap: 'wrap'}}>
      <Tooltip title={ytTooltip}>
        <Chip
          label={ytTone.label}
          size={size}
          sx={{
            height: size === 'small' ? 22 : undefined,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: ytTone.bg,
            color: ytTone.fg,
          }}
        />
      </Tooltip>
      <Tooltip title={dtTooltip}>
        <Chip
          label={dtTone.label}
          size={size}
          sx={{
            height: size === 'small' ? 22 : undefined,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: dtTone.bg,
            color: dtTone.fg,
          }}
        />
      </Tooltip>
    </Box>
  );
}
